import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import {catchError, map, Subject} from "rxjs";
import {ExternalProfile, ExternalProfileEndpointResponse} from "../models/external-profile.model";
import {FeedbackService} from "./feedback.service";
import {Study, StudyEndpointResponse} from "../models/metadata";

const ROUTES = {
  'external-profiles': '/api/options/external',
  'studies': '/api/options/studies',
  'persons': '/api/options/persons',
  'targets': '/api/options/targets'
}

export type PersonOptionsEndpointResponse = {
  persons: Array<string>
}

export type TargetOptionsEndpointResponse = {
  targets: Array<string>
}

const DEFAULT_EXTERNAL_PROFILE_RESPONSE = {
  externalProfiles: []
} as ExternalProfileEndpointResponse

const DEFAULT_STUDY_RESPONSE = {
  studies: []
} as StudyEndpointResponse

const DEFAULT_PERSON_RESPONSE = {
  persons: []
} as PersonOptionsEndpointResponse

const DEFAULT_TARGET_RESPONSE = {
  targets: []
} as TargetOptionsEndpointResponse


@Injectable({
  providedIn: 'root'
})
export class AutocompletionOptionsService {

  constructor(private http: HttpClient, private feedbackService: FeedbackService) { }

  getExternalProfiles(query: string){
    const responseWrapperSubject = new Subject<ExternalProfile[]>()
    const params = new HttpParams().set('filterQuery', query || "")
    const observable = this.http.get<ExternalProfileEndpointResponse>(ROUTES['external-profiles'], {params})
      .pipe(
        catchError(this.feedbackService.handleError('getExternalProfiles', DEFAULT_EXTERNAL_PROFILE_RESPONSE)),
        map(res => this.parseExternalProfileResponse(res))
      )

    observable.subscribe(res => {
      responseWrapperSubject.next(res)
    })

    return responseWrapperSubject.asObservable()
  }

  getStudies(query: string){
    const responseWrapperSubject = new Subject<Study[]>()
    const params = new HttpParams().set('filterQuery', query || "")
    const observable = this.http.get<StudyEndpointResponse>(ROUTES['studies'], {params})
      .pipe(
        catchError(this.feedbackService.handleError('get study options', DEFAULT_STUDY_RESPONSE)),
        map(res => res.studies)
      )

    observable.subscribe(res => {
      responseWrapperSubject.next(res)
    })

    return responseWrapperSubject.asObservable()
  }

  getPersons(query: string){
    const responseWrapperSubject = new Subject<string[]>()
    const params = new HttpParams().set('filterQuery', query || "")
    const observable = this.http.get<PersonOptionsEndpointResponse>(ROUTES['persons'], {params})
      .pipe(
        catchError(this.feedbackService.handleError('get authors/external profiles options', DEFAULT_PERSON_RESPONSE)),
        map(res => res.persons)
      )

    observable.subscribe(res => {
      responseWrapperSubject.next(res)
    })

    return responseWrapperSubject.asObservable()
  }

  getTargets(query: string){
    const responseWrapperSubject = new Subject<string[]>()
    const params = new HttpParams().set('filterQuery', query || "")
    const observable = this.http.get<TargetOptionsEndpointResponse>(ROUTES['targets'], {params})
      .pipe(
        catchError(this.feedbackService.handleError('get target options', DEFAULT_TARGET_RESPONSE)),
        map(res => res.targets)
      )

    observable.subscribe(res => {
      responseWrapperSubject.next(res)
    })

    return responseWrapperSubject.asObservable()
  }

  private parseExternalProfileResponse(res: ExternalProfileEndpointResponse) {
    return res.externalProfiles.map(ep => Object.assign(new ExternalProfile(), ep))
  }
}
